import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import {
  acknowledgePermissionChangeAPI,
  checkPermissionChangeAPI,
} from '../api/auth.api';
import { addPermissionChangeListener, useUserStore } from '../store/userStore';
import { usePermission } from './usePermission';

/**
 * 사용자 권한 변경을 감지하고 관련 데이터를 새로고침하는 훅
 */
export const usePermissionWatcher = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission, permissions } = usePermission();
  const [isChecking, setIsChecking] = useState(false);
  const [hasPermissionChange, setHasPermissionChange] = useState(false);

  const previousPermissionsRef = useRef<string | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const user = useUserStore((state) => state.user);

  /**
   * 페이지 진입 시 권한 확인
   */
  const checkPermissionsOnPageEnter = useCallback(
    async (pageName?: string) => {
      const currentUser = useUserStore.getState().user;

      if (
        !currentUser ||
        !currentUser.permissions ||
        currentUser.permissions.length === 0
      ) {
        console.log('[usePermissionWatcher] 권한 정보 로딩 중...');
        return;
      }

      setIsChecking(true);

      try {
        console.log(
          `[usePermissionWatcher] ${pageName || '페이지'} 진입 시 권한 확인 중...`
        );

        // 서버에서 권한 변경 확인
        const response = await checkPermissionChangeAPI();

        if (response.hasChange && response.notification) {
          console.log(
            '[usePermissionWatcher] 권한 변경 감지됨:',
            response.notification
          );

          setHasPermissionChange(true);

          // 권한 변경 알림
          const changeTypeMap = {
            role_update: '역할이 변경',
            permission_update: '권한이 변경',
            access_revoked: '접근 권한이 취소',
          };

          const changeMessage =
            changeTypeMap[response.notification.changeType] || '권한이 변경';

          message.warning({
            content: `${changeMessage}되었습니다. 페이지를 새로고침해주세요.`,
            duration: 10,
            onClick: () => handlePermissionChangeAcknowledge(),
          });

          // 메뉴 및 관련 데이터 새로고침
          refreshAllPermissionData();
        } else {
          console.log('[usePermissionWatcher] 권한 변경 없음');
        }
      } catch (error) {
        console.error('[usePermissionWatcher] 권한 확인 실패:', error);
        // 권한 확인 실패해도 페이지는 정상 진행
      } finally {
        setIsChecking(false);
      }
    },
    [] // 의존성 제거하고 내부에서 최신 상태 직접 조회
  );

  /**
   * 권한 변경 알림 확인 처리
   */
  const handlePermissionChangeAcknowledge = useCallback(async () => {
    try {
      await acknowledgePermissionChangeAPI();
      setHasPermissionChange(false);

      // 페이지 새로고침
      window.location.reload();
    } catch (error) {
      console.error('[usePermissionWatcher] 권한 변경 알림 확인 실패:', error);
    }
  }, []);

  /**
   * 모든 권한 관련 데이터 새로고침
   */
  const refreshAllPermissionData = useCallback(() => {
    console.log('[usePermissionWatcher] 모든 권한 관련 데이터 새로고침 중...');

    // 메뉴 관련 쿼리 무효화
    queryClient.invalidateQueries({ queryKey: ['admin-menu'] });
    queryClient.invalidateQueries({ queryKey: ['partner-menu'] });

    // 사용자 권한 관련 쿼리 무효화
    queryClient.invalidateQueries({ queryKey: ['userPermissions'] });
    queryClient.invalidateQueries({ queryKey: ['userInfo'] });

    // 기타 권한 기반 데이터 무효화
    queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    queryClient.invalidateQueries({ queryKey: ['users'] });

    console.log('[usePermissionWatcher] 권한 관련 데이터 새로고침 완료');
  }, [queryClient]);

  /**
   * 주기적 권한 변경 확인 (5분마다)
   */
  const startPeriodicCheck = useCallback(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }

    checkIntervalRef.current = setInterval(
      async () => {
        const currentUser = useUserStore.getState().user;
        if (!currentUser || !currentUser.permissions) return;

        try {
          const response = await checkPermissionChangeAPI();

          if (response.hasChange && response.notification) {
            console.log('[usePermissionWatcher] 주기적 권한 변경 감지');

            // 자동 새로고침은 하지 않고 알림만 표시
            message.info({
              content: '권한이 변경되었습니다. 새로고침을 권장합니다.',
              duration: 0, // 자동으로 사라지지 않음
              key: 'permission-change',
              onClick: () => {
                message.destroy('permission-change');
                handlePermissionChangeAcknowledge();
              },
            });
          }
        } catch (error) {
          console.error('[usePermissionWatcher] 주기적 권한 확인 실패:', error);
        }
      },
      5 * 60 * 1000
    ); // 5분마다
  }, [handlePermissionChangeAcknowledge]); // user 의존성 제거

  /**
   * 로컬 권한 변경 감지 (Zustand store 변경)
   */
  useEffect(() => {
    if (!user || !user.permissions) return;

    // 현재 권한들을 정규화하여 문자열로 변환
    const currentPermissions = user.permissions
      .map((p: any) => (typeof p === 'object' && p.name ? p.name : String(p)))
      .filter(Boolean)
      .sort();

    const currentPermissionsString = JSON.stringify(currentPermissions);

    // 초기 설정
    if (previousPermissionsRef.current === null) {
      previousPermissionsRef.current = currentPermissionsString;
      console.log(
        '[usePermissionWatcher] 초기 권한 상태 설정:',
        currentPermissions.length,
        '개'
      );
      return;
    }

    // 권한 변경 감지
    if (previousPermissionsRef.current !== currentPermissionsString) {
      console.log(
        '[usePermissionWatcher] 로컬 권한 변경 감지 - 데이터 새로고침'
      );

      const previousPermissions = JSON.parse(previousPermissionsRef.current);
      const addedPermissions = currentPermissions.filter(
        (p: any) => !previousPermissions.includes(p)
      );
      const removedPermissions = previousPermissions.filter(
        (p: any) => !currentPermissions.includes(p)
      );

      console.log('[usePermissionWatcher] 권한 변경 상세:', {
        추가: addedPermissions,
        제거: removedPermissions,
        총권한수: currentPermissions.length,
      });

      // 데이터 새로고침
      refreshAllPermissionData();

      // 권한이 추가/제거된 경우 알림
      if (addedPermissions.length > 0) {
        message.success(
          `새로운 권한이 추가되었습니다. (${addedPermissions.length}개)`
        );
      }
      if (removedPermissions.length > 0) {
        message.warning(
          `일부 권한이 제거되었습니다. (${removedPermissions.length}개)`
        );
      }
    }

    // 현재 권한을 이전 권한으로 저장
    previousPermissionsRef.current = currentPermissionsString;
  }, [user?.permissions, refreshAllPermissionData]);

  /**
   * 권한 변경 이벤트 리스너 등록
   */
  useEffect(() => {
    const removeListener = addPermissionChangeListener((updatedUser) => {
      console.log('[usePermissionWatcher] 전역 권한 변경 이벤트 수신');
      refreshAllPermissionData();
    });

    return removeListener;
  }, [refreshAllPermissionData]);

  /**
   * 컴포넌트 마운트 시 주기적 체크 시작
   */
  useEffect(() => {
    const currentUser = useUserStore.getState().user;
    if (
      currentUser &&
      currentUser.permissions &&
      currentUser.permissions.length > 0
    ) {
      startPeriodicCheck();
    }

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [startPeriodicCheck]); // user 의존성 제거하고 startPeriodicCheck만 의존

  /**
   * 특정 권한 요구사항 체크
   */
  const checkRequiredPermissions = useCallback(
    (requiredPermissions: string[]) => {
      if (!user || !permissions) {
        console.warn('[usePermissionWatcher] 사용자 정보 또는 권한 정보 없음');
        return false;
      }

      const hasAllPermissions = requiredPermissions.every((permission) =>
        hasPermission(permission)
      );

      if (!hasAllPermissions) {
        const missingPermissions = requiredPermissions.filter(
          (permission) => !hasPermission(permission)
        );

        console.warn('[usePermissionWatcher] 필요 권한 부족:', {
          필요권한: requiredPermissions,
          부족권한: missingPermissions,
        });

        message.error('이 기능을 사용할 권한이 없습니다.');
        return false;
      }

      return true;
    },
    [user, permissions, hasPermission]
  );

  /**
   * 권한 없음 페이지로 리다이렉트
   */
  const redirectToUnauthorized = useCallback(() => {
    message.error('접근 권한이 없습니다.');
    navigate('/unauthorized', { replace: true });
  }, [navigate]);

  return {
    // 상태
    isChecking,
    hasPermissionChange,

    // 함수
    checkPermissionsOnPageEnter,
    handlePermissionChangeAcknowledge,
    refreshAllPermissionData,
    checkRequiredPermissions,
    redirectToUnauthorized,

    // 유틸리티
    refreshMenu: refreshAllPermissionData, // 하위 호환성
  };
};
